const firstValue = (source, keys, fallback = '') => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
};

const clean = (value) => String(value ?? '').trim();
const upper = (value) => clean(value).toUpperCase();
const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const statusFromTimes = (source, now) => {
  const expiredAt = toDate(firstValue(source, ['expired_at', 'expiry_date', 'expiry_at', 'use_within']));
  const mustUseAt = toDate(firstValue(source, ['must_use_at', 'must_use_date']));
  const readyAt = toDate(firstValue(source, ['ready_at', 'finished_time', 'finished_at']));

  if (expiredAt && expiredAt <= now) return 'EXPIRED';
  if (mustUseAt && mustUseAt <= now) return 'MUST USE';
  if (readyAt && readyAt > now) return 'PREPARING';
  return 'READY';
};

const normalizeStatus = (value, source, now) => {
  const normalized = upper(value).replace(/[_-]+/g, ' ');
  if (['EXPIRED', 'MUST USE', 'READY', 'PREPARING'].includes(normalized)) return normalized;
  return statusFromTimes(source, now);
};

const normalizeCategory = (value) => {
  const normalized = upper(value).replace(/[_-]+/g, ' ');
  if (normalized.includes('RAW')) return 'RAW MAT';
  if (normalized === 'FG' || normalized.includes('FINISH')) return 'FG';
  return 'WIP';
};

export const normalizeWipStockDocument = (source, now = new Date()) => {
  const createdAt = toDate(firstValue(source, [
    'created_at', 'createdAt', 'registered_at', 'start_time', 'timestamp', 'updated_at',
  ]));
  const agingStartedAt = toDate(firstValue(source, ['ready_at', 'finished_at'])) || createdAt;
  const explicitAging = firstValue(source, ['aging_hours', 'agingHours', 'time_hours'], null);
  const agingHours = explicitAging === null
    ? Math.max(0, agingStartedAt ? (now.getTime() - agingStartedAt.getTime()) / 3600000 : 0)
    : Math.max(0, numberValue(explicitAging));

  return {
    id: clean(firstValue(source, ['id', 'source_register_id', 'register_id'])),
    plantId: clean(firstValue(source, ['plant_id', 'plantId', 'PLANT_ID', 'plant'])),
    factory: clean(firstValue(source, ['factory', 'FACTORY'])),
    line: clean(firstValue(source, ['line', 'LINE'])),
    zone: clean(firstValue(source, ['zone', 'ZONE'])),
    location: clean(firstValue(source, ['location', 'storage_location', 'zone', 'LOCATION'], '-')),
    sku: clean(firstValue(source, ['sku', 'product_code', 'mat_code', 'material_code', 'SKU'], '-')),
    skuName: clean(firstValue(source, ['sku_name', 'product_name', 'material_description', 'name'], '-')),
    step: clean(firstValue(source, ['step', 'process_step', 'process_step_full'], '-')),
    batch: clean(firstValue(source, ['batch', 'batch_no'], '-')),
    lot: clean(firstValue(source, ['lot', 'lot_no'], '-')),
    weightKg: numberValue(firstValue(source, ['weight_kg', 'weight', 'net_weight'])),
    agingHours,
    status: normalizeStatus(firstValue(source, ['status', 'aging_status']), source, now),
    category: normalizeCategory(firstValue(source, ['stock_type', 'category', 'product_type', 'type'])),
    createdAt,
    raw: source,
  };
};

const sameScope = (actual, expected) => !clean(expected) || upper(actual) === upper(expected);

export const normalizeWipStock = (documents, scope, now = new Date()) => (
  documents
    .map((document) => normalizeWipStockDocument(document, now))
    .filter((item) => (
      sameScope(item.plantId, scope.plantId)
      && sameScope(item.factory, scope.factory)
      && item.raw?.in_stock !== false
      && item.raw?.active !== false
      && item.raw?.is_deleted !== true
    ))
    .sort((left, right) => (right.createdAt?.getTime() || 0) - (left.createdAt?.getTime() || 0))
);

const uniqueCount = (items, selector) => new Set(items.map(selector).filter(Boolean)).size;
const round = (value) => Math.round(value * 100) / 100;
const statusOrder = ['EXPIRED', 'MUST USE', 'READY', 'PREPARING'];
export const sortWipByStatus = (items) => [...items].sort((a, b) =>
  (statusOrder.indexOf(a.status) < 0 ? 99 : statusOrder.indexOf(a.status))
  - (statusOrder.indexOf(b.status) < 0 ? 99 : statusOrder.indexOf(b.status))
);

export const buildWipMonitorSummary = (items) => {
  const totalKg = items.reduce((sum, item) => sum + item.weightKg, 0);
  const storage = ['RAW MAT', 'WIP', 'FG'].reduce((result, category) => ({
    ...result,
    [category]: round(items
      .filter((item) => item.category === category)
      .reduce((sum, item) => sum + item.weightKg, 0)),
  }), {});

  const areaMap = items.reduce((result, item) => {
    const key = item.location || '-';
    if (!result.has(key)) result.set(key, []);
    result.get(key).push(item);
    return result;
  }, new Map());

  const areas = [...areaMap.entries()].map(([name, rows]) => ({
    name,
    totalKg: round(rows.reduce((sum, row) => sum + row.weightKg, 0)),
    sku: uniqueCount(rows, (row) => row.sku),
    batch: uniqueCount(rows, (row) => `${row.lot}|${row.batch}`),
    expired: rows.filter((row) => row.status === 'EXPIRED').length,
    mustUse: rows.filter((row) => row.status === 'MUST USE').length,
  })).sort((left, right) => right.totalKg - left.totalKg);

  const denominator = Math.max(1, uniqueCount(items, (item) => item.sku));
  const agingStatus = statusOrder.map((status) => {
    const count = uniqueCount(items.filter((item) => item.status === status), (item) => item.sku);
    return { status, count, percent: Math.round((count / denominator) * 100) };
  });

  const priority = { EXPIRED: 0, 'MUST USE': 1, READY: 2, PREPARING: 3 };
  const agingAlerts = [...items]
    .sort((left, right) => (
      (priority[left.status] ?? 9) - (priority[right.status] ?? 9)
      || right.agingHours - left.agingHours
    ))
    .slice(0, 6);

  return { totalKg: round(totalKg), storage, areas, agingStatus, agingAlerts };
};

export const formatMonitorNumber = (value, maximumFractionDigits = 2) => (
  Number(value || 0).toLocaleString('en-US', { maximumFractionDigits })
);

